import { useRef, useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { CreateMentionNotificationMutation } from '../graphql/CreateMentionNotificationMutation.graphql';

type CreateMentionNotificationMutationProps = {
	contentId: string;
	showErrorFlag: (displayName: string) => void;
};

type SendCreateMentionNotificationProps = {
	mentionedUserId: string;
	displayName: string;
	mentionLocalId: string;
};

export const useCreateMentionNotificationMutation = ({
	contentId,
	showErrorFlag,
}: CreateMentionNotificationMutationProps) => {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>();
	const [createMentionNotificationMutation] = useMutation(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		CreateMentionNotificationMutation,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendCreateMentionNotification = useCallback(
		({ mentionedUserId, displayName, mentionLocalId }: SendCreateMentionNotificationProps) => {
			const fireAnalyticsEvent = (action: 'succeeded' | 'failed') => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'mentionsNotifications',
						action,
						actionSubject: 'mentionInputMutation',
						attributes: {
							contentId,
							mentionedUserId,
							mentionLocalId,
						},
					},
				}).fire();
			};

			// NCS draft syncs need to happen prior to mutation in order for
			// email content to show up. A 15 second buffer after user closes
			// the flag notification will give draft syncs a chance to happen.
			timerRef.current = setTimeout(() => {
				createMentionNotificationMutation({
					variables: {
						contentId,
						mentionedUserAccountId: mentionedUserId,
						mentionLocalId,
					},
				})
					.then(() => {
						fireAnalyticsEvent('succeeded');
					})
					.catch(() => {
						showErrorFlag(displayName);
						fireAnalyticsEvent('failed');
					})
					.finally(() => {
						timerRef.current = null;
					});
			}, 15000);
		},
		[contentId, createAnalyticsEvent, createMentionNotificationMutation, showErrorFlag],
	);

	return {
		sendCreateMentionNotification,
	};
};
